import React from 'react'
import { useLocalStorage } from 'usehooks-ts'

export type WorkMode = 'remote' | 'hybrid' | 'onsite' | ''
export type ApplicationStatus =
  | 'applied'
  | 'interviewing'
  | 'offered'
  | 'rejected'
  | 'withdrawn'
  | ''

export interface JobApplication {
  id: string
  date: string
  position: string
  company: string
  workMode: WorkMode
  location: string
  salary: string
  url: string
  status: ApplicationStatus
  notes: string
  resume: string
}

const STORAGE_KEY = 'resume-alpha-job-log'

export const useJobLog = () => {
  const [applications, setApplications] = useLocalStorage<JobApplication[]>(
    STORAGE_KEY,
    [],
  )

  const addApplication = React.useCallback(
    (application: Omit<JobApplication, 'id'>) => {
      const newApplication: JobApplication = {
        ...application,
        id: crypto.randomUUID(),
      }
      setApplications((prev) => [newApplication, ...prev])
      return newApplication
    },
    [setApplications],
  )

  const updateApplication = React.useCallback(
    (id: string, updates: Partial<Omit<JobApplication, 'id'>>) => {
      setApplications((prev) =>
        prev.map((app) => (app.id === id ? { ...app, ...updates } : app)),
      )
    },
    [setApplications],
  )

  const deleteApplication = React.useCallback(
    (id: string) => {
      setApplications((prev) => prev.filter((app) => app.id !== id))
    },
    [setApplications],
  )

  const exportToCSV = React.useCallback(() => {
    if (applications.length === 0) return

    const headers = [
      'Date',
      'Position',
      'Company',
      'Work Mode',
      'Location',
      'Salary',
      'URL',
      'Status',
      'Notes',
    ]

    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    const rows = applications.map((app) => [
      escapeCSV(app.date),
      escapeCSV(app.position),
      escapeCSV(app.company),
      escapeCSV(app.workMode),
      escapeCSV(app.location),
      escapeCSV(app.salary),
      escapeCSV(app.url),
      escapeCSV(app.status),
      escapeCSV(app.notes),
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `job-applications-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [applications])

  const exportToJSON = React.useCallback(() => {
    if (applications.length === 0) return

    const dataToExport = applications.map(({ id, ...rest }) => rest)
    const jsonContent = JSON.stringify(dataToExport, null, 2)

    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `job-applications-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [applications])

  return {
    applications,
    addApplication,
    updateApplication,
    deleteApplication,
    exportToCSV,
    exportToJSON,
  }
}
